<template>
  <div :class="containerClass">
    <div class="relative" :class="spinnerSizeClass">
      <div :class="ringTrackClass"></div>
      <div :class="ringSpinnerClass"></div>
      <div class="absolute inset-0 flex items-center justify-center">
        <IconActivity :class="iconSizeClass" />
      </div>
    </div>
    <p v-if="message" class="text-center text-sm font-medium text-gray-600 dark:text-slate-400">{{ message }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import IconActivity from "@core/icons/IconActivity.vue";

interface Props {
  size?: "small" | "medium" | "large";
  message?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: "medium",
  message: undefined
});

const hasMessage = computed(() => Boolean(props.message));
const showTrack = computed(() => props.size !== "small");

const sizeMap = {
  small: {
    spinner: "h-5 w-5",
    ring: "border-2",
    icon: "h-3 w-3"
  },
  medium: {
    spinner: "h-10 w-10",
    ring: "border-4",
    icon: "h-4 w-4"
  },
  large: {
    spinner: "h-14 w-14",
    ring: "border-4",
    icon: "h-5 w-5"
  }
} as const;

const resolvedSize = computed(() => sizeMap[props.size] ?? sizeMap.medium);

const spinnerSizeClass = computed(() => resolvedSize.value.spinner);
const iconSizeClass = computed(() => resolvedSize.value.icon);

const ringTrackClass = computed(() => {
  const ringWidth = resolvedSize.value.ring;
  if (!showTrack.value) {
    return "hidden";
  }
  return [
    "absolute inset-0 rounded-full border-solid",
    ringWidth,
    "border-emerald-200/60 dark:border-emerald-900/50"
  ].join(" ");
});

const ringSpinnerClass = computed(() => {
  const ringWidth = resolvedSize.value.ring;
  return [
    "absolute inset-0 rounded-full border-solid animate-spin",
    ringWidth,
    "border-emerald-200/30 dark:border-emerald-900/30 border-t-emerald-600 dark:border-t-emerald-400"
  ].join(" ");
});

const containerClass = computed(() => {
  const base = "inline-flex items-center justify-center text-emerald-600 dark:text-emerald-400";
  const withMessage = hasMessage.value ? "flex-col gap-3 py-4 px-3" : "";
  return [base, withMessage].filter(Boolean).join(" ");
});
</script>
